/**
 * The loaded translation tables, and how to load one.
 *
 * Its own module with NO imports of app code, on purpose: `@/i18n` imports the
 * locale provider, so a test that mocks the provider gets a fresh `@/i18n` — and
 * a store kept there would come back empty, while this one is shared.
 *
 * A language is loaded on first use, never in the entry file (step 14,
 * 2026-10-05): Hebrew's tables were 155 kB of source in every visitor's first
 * download for a reader who never chose Hebrew. Since step 73 there are many
 * languages, so none is PRECACHED either (`offline/swPlugin.ts`): the one a
 * person reads is fetched once, kept by the service worker's shell cache, and
 * works offline from then on.
 *
 * **A language is a folder** — `src/i18n/<language>/index.ts` exporting
 * `dictionary` — found by `import.meta.glob`, so adding one is adding its
 * folder (and its code to `UI_LANGUAGES`). `i18n.test.ts` holds every folder
 * to the full set of keys.
 */
import { languageOf, type Locale } from '@myrave/shared';

/**
 * A count-dependent sentence: one form per CLDR plural category the language
 * has (`Intl.PluralRules`) — Hebrew's `two`, Arabic's six. `other` always.
 */
export type PluralForms = Readonly<Partial<Record<Intl.LDMLPluralRule, string>>> & { readonly other: string };
export type Entry = string | PluralForms;
export type Dictionary = Readonly<Record<string, Entry>>;

const MODULES = import.meta.glob<{ dictionary: Dictionary }>('./*/index.ts');

const LOADERS: Partial<Record<string, () => Promise<Dictionary>>> = Object.fromEntries(
  Object.entries(MODULES).map(([path, load]) => [
    path.split('/')[1]!,
    () => load().then((m) => m.dictionary),
  ]),
);
const LOADED: Partial<Record<string, Dictionary>> = {};

/** The languages that have a dictionary — English has none, it is the keys. */
export const DICTIONARY_LANGUAGES: readonly string[] = Object.keys(LOADERS);

/** The table for a locale, if it has been loaded; English has none. */
export function dictionaryFor(locale: Locale): Dictionary | undefined {
  // `he-IL` and `he-US` read the same words: the dictionary is the LANGUAGE's.
  return LOADED[languageOf(locale)];
}

/** True when `translate(locale, …)` can answer in that language right now. */
export function dictionaryReady(locale: Locale): boolean {
  const language = languageOf(locale);
  return !LOADERS[language] || Boolean(LOADED[language]);
}

/** Fetch a locale's table; a no-op for English and for one already loaded. */
export async function loadDictionary(locale: Locale): Promise<void> {
  const language = languageOf(locale);
  const load = LOADERS[language];
  if (!load || LOADED[language]) return;
  LOADED[language] = await load();
}
